// src/pages/PatientAppointmentDetailsPage.tsx
import DesktopHeader from "../components/defaultHeader/DesktopHeader";
import Heading from "../components/typography/Heading";
import colors from "../utils/colors";
import { Badge, Box, Flex, Select, Text } from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import SubHeading from "../components/typography/SubHeading";
import { DoctorIcon } from "../assets";
import PatientAppointmentCard from "../features/patients/components/PatientAppointmentCard";
import AppointmentComplaintsCard from "../features/patients/components/AppointmentComplaintsCard";
import MedicalHistoryPage from "./MedicalHistoryPage";
import {
  useActiveAdmission,
  useGetAppointmentComplaint,
  useGetBodyParts,
  useGetPatientDetails,
  useSaveComplaint,

  // 🔥 NEW: rich appointment hooks (same as AppointmentDetails.tsx)
  useGetAppointmentFamilyHistory,
  useGetAppointmentSocialHistory,
  useGetAppointmentPastMedicalHistory,
  useGetAppointmentMedicationHistory,
  useGetMediaByAppointment,
  useGetProvisionalDifferentialDiagnosis,
  useGetDefinitiveDiagnosis,
  useGetTreatmentsByAppointmentId,
  useGetPhysicalFindingsNotes,
  useGetOrderedTestsByAppointment,
} from "../apis/patients";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import Button from "../components/ui/button";
import { useFormik } from "formik";
import * as yup from "yup";
import customToast, { ToastType } from "../components/Toast";
import { useNavigate } from "react-router-dom";
import React from "react";
import PatientAdmissionCard from "../features/admissions/PatientAdmissionCard";
import { buildAdmissionSeedFromAppointment } from "../utils/admissionSeed"; // 🔥 NEW
import { useGetDoctorNamesByIds } from "../apis/doctor";

interface Complaint {
  selectedBodyPart: string;
  selectedComplaint: string;
  selectedSymptom: string;
  note: string;
}

interface FormValues {
  mainComplaint: Complaint;
  otherComplaints: Complaint[];
}

const complaintValidation = yup
  .object()
  .test(
    "complaint-validation",
    "Invalid complaint input.",
    function (value: Partial<Complaint> | undefined) {
      const {
        selectedBodyPart = "",
        selectedComplaint = "",
        selectedSymptom = "",
        note = "",
      } = value || {};

      const hasPickableInput =
        selectedBodyPart || selectedComplaint || selectedSymptom;
      const hasNoteInput = note;

      if (!hasPickableInput && !hasNoteInput) {
        return this.createError({
          message:
            "Please enter notes or select Body Part, Complaint, and Symptom.",
        });
      }

      if (
        hasPickableInput &&
        (!selectedBodyPart || !selectedComplaint || !selectedSymptom)
      ) {
        return this.createError({
          message:
            "Body Part, Complaint, and Symptom are all required if any is selected.",
        });
      }

      return true;
    },
  )
  .required();

const PatientAppointmentDetailsPage = () => {
  const mapAgeToRange = (age: number) => {
    if (age < 1) return "i";
    if (age >= 1 && age <= 5) return "u";
    if (age >= 6 && age <= 12) return "c";
    if (age >= 13 && age <= 19) return "t";
    if (age >= 20 && age <= 45) return "ya";
    if (age >= 46 && age <= 59) return "ma";
    if (age >= 60) return "ea";
  };

  const mapGender = (gender: string) =>
    gender.toLowerCase() === "female" ? "f" : "m";

  const [pregnancyStatus, setPregnancyStatus] = useState<
    "Yes" | "No" | "Not sure" | ""
  >("");

  const mapPregnancyStatus = (status: string) => {
    switch (status) {
      case "No":
        return "notp";
      case "Yes":
        return "pplus";
      case "Not sure":
        return "undefp";
      default:
        return "";
    }
  };

  const navigate = useNavigate();

  const location = useLocation();
  const patientIdFromRoute = React.useMemo(
    () => location?.pathname?.split("/")?.[2] ?? "",
    [location?.pathname],
  );

  const { data, error, isLoading } = useGetPatientDetails({
    patientId: patientIdFromRoute,
  });

  // IDs of doctors this patient has been referred to (from patient record)
  const referredIds = data?.data?.referredTo || [];

  // Fetch their names/emails so the seed text can show proper names
  const { data: referralDoctors } = useGetDoctorNamesByIds(referredIds);

  const [objectIdPatientId, setObjectIdPatientId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (data?.data?.patientId && data?.data?._id) {
      setObjectIdPatientId(data.data._id);
    }
  }, [data]);

  const patientGender = data?.data?.gender || "Unknown";
  const patientAge = data?.data?.age;

  // ⭐ Appointment ID from patient details
  // const appointmentId = data?.data?.appointments[0]?.id || "Default ID";

  // ⭐ Appointment ID from route

  const appointmentId = React.useMemo(
    () => location?.pathname?.split("/")?.[4] ?? "",
    [location?.pathname],
  );

  // === EXISTING HOOKS ===
  const { mutate: saveComplaint, isPending } = useSaveComplaint();
  const { data: appointmentComplaint } =
    useGetAppointmentComplaint(appointmentId);
  const { data: bodyParts, isLoading: loadingBodyParts } = useGetBodyParts({
    age: patientAge,
  });

  // === 🔥 NEW: rich appointment data hooks (same as AppointmentDetails.tsx) ===
  const { data: pastMedicalHistory } =
    useGetAppointmentPastMedicalHistory(appointmentId);
  const { data: familyHistory } = useGetAppointmentFamilyHistory(appointmentId);
  const { data: socialHistory } = useGetAppointmentSocialHistory(appointmentId);
  const { data: medicationHistory } =
    useGetAppointmentMedicationHistory(appointmentId);
  const { data: mediaData } = useGetMediaByAppointment(appointmentId);
  const { data: provisionalDiagnosisData } =
    useGetProvisionalDifferentialDiagnosis(appointmentId);
  const { data: definitiveDiagnosisData } =
    useGetDefinitiveDiagnosis(appointmentId);
  const { data: treatmentData } =
    useGetTreatmentsByAppointmentId(appointmentId);
  const { data: physicalFindingsData } =
    useGetPhysicalFindingsNotes(appointmentId);
  const { data: orderedTestsData } =
    useGetOrderedTestsByAppointment(appointmentId);

  const referralData = data?.data?.referredTo || [];

  const filterBodyPartByCategory = (value, category) => {
    const it = bodyParts?.find((item) => item.name === value)?.[category];
    return it;
  };

  const validateDuplicateOtherComplaint = (newComplaint) => {
    return appointmentComplaint?.data?.otherComplaints?.some(
      (existingComplaint) =>
        existingComplaint.bodyPart === newComplaint.selectedBodyPart &&
        existingComplaint.complaint === newComplaint.selectedComplaint &&
        existingComplaint.cond === newComplaint.selectedSymptom &&
        existingComplaint.note === newComplaint.note,
    );
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      mainComplaint: {
        selectedBodyPart: "",
        selectedComplaint: "",
        selectedSymptom: "",
        note: "",
      },
      otherComplaints: [],
    },

    validationSchema: yup.object({
      mainComplaint: complaintValidation,
      otherComplaints: yup.array().of(complaintValidation),
    }),

    onSubmit: (values) => {
      const mappedAge = mapAgeToRange(patientAge);
      const mappedGender = mapGender(patientGender);
      const pregnancyCondition =
        patientGender === "Female" ? mapPregnancyStatus(pregnancyStatus) : "";

      if (selectedComplaint === "Main complaint") {
        const existingMainComplaint = appointmentComplaint?.data?.mainComplaint;

        if (!existingMainComplaint && selectedComplaint === "Main complaint") {
          const mainComplaintPayload = {
            appointmentIdString: appointmentId,
            gender: mappedGender,
            age: mappedAge,
            mainComplaint: {
              mainComplaint: values?.mainComplaint?.selectedSymptom || "",
              mainComplaintBodyPart: values?.mainComplaint?.selectedBodyPart
                ? filterBodyPartByCategory(
                    values?.mainComplaint?.selectedBodyPart,
                    "main",
                  )
                : "",
              cond: pregnancyCondition,
              mainComplaintNote: values?.mainComplaint?.note || "",
            },
            otherComplaints: [],
          };

          if (!isPending) {
            saveComplaint(mainComplaintPayload);
          }
        }
      } else if (selectedComplaint === "Other complaints") {
        const duplicates = values.otherComplaints.filter((complaint) =>
          validateDuplicateOtherComplaint(complaint),
        );

        if (duplicates.length > 0) {
          customToast(
            "Duplicate complaints are not allowed.",
            ToastType.warning,
          );
          return;
        }

        const payload = {
          appointmentIdString: appointmentId,
          gender: mappedGender,
          age: mappedAge,
          otherComplaints: values?.otherComplaints?.map((item) => ({
            complaint: item?.selectedSymptom || "",
            bodyPart: item?.selectedBodyPart
              ? filterBodyPartByCategory(item?.selectedBodyPart, "other")
              : "",
            cond: pregnancyCondition || "",
            note: item?.note || "",
          })),
        };
        saveComplaint(payload);
      }
    },
  });

  const patientAppointmentDetails = [
    {
      image: DoctorIcon,
      title: "Doctor",
      value: data?.data?.appointments[0]?.doctorName || "Dr Catherine",
    },
    {
      image: DoctorIcon,
      title: "Time",
      value: data?.data?.appointments[0]?.startTime
        ? moment(data.data.appointments[0].startTime)
            ?.subtract(1, "hour")
            ?.format("h:mma")
        : "9:00 AM",
    },
    {
      image: DoctorIcon,
      title: "Date",
      value: data?.data?.appointments[0]?.date
        ? moment(data.data.appointments[0].date).format("YYYY-MM-DD")
        : "2024-11-01",
    },
  ];

  const [selectedComplaint, setSelectedComplaint] = useState("Main complaint");
  const [mainComplaintValid, setMainComplaintValid] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState("Main complaint");

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;

    setSelectedComplaint(value);
    setSelectedHistory(value);
  };

  const { data: activeAdmission, isFetching } =
    useActiveAdmission(patientIdFromRoute);

  const handleDeleteComplaint = (index: number) => {
    formik.setFieldValue(
      "otherComplaints",
      formik?.values?.otherComplaints.filter((_, i) => i !== index),
    );
  };

  // 🔥 RICH seed text from this appointment (same power as AppointmentDetails)
  const seedText = React.useMemo(() => {
    const normalizedComplaints =
      appointmentComplaint?.data ?? appointmentComplaint;

    const rawSeed = buildAdmissionSeedFromAppointment({
      patientName: data?.data?.name,
      patientId: patientIdFromRoute,
      appointmentId,
      complaintsData: normalizedComplaints,
      physicalFindingsData,
      provisionalDiagnosisData,
      definitiveDiagnosisData,
      treatmentData,
      orderedTestsData,
      pastMedicalHistory,
      familyHistory,
      socialHistory,
      medicationHistory,
      mediaData,
      referralData: referralDoctors,
    });

    // Fallback to a simple line if for some reason everything is empty
    if (!rawSeed || !rawSeed.trim()) {
      return (
        `Preliminary admission notes for patient ` +
        `${data?.data?.name ?? "Unknown"} ` +
        `(${patientIdFromRoute || "No ID"}) at ${new Date().toLocaleString()}`
      );
    }

    return rawSeed;
  }, [
    data?.data?.name,
    patientIdFromRoute,
    appointmentId,
    appointmentComplaint,
    physicalFindingsData,
    provisionalDiagnosisData,
    definitiveDiagnosisData,
    treatmentData,
    orderedTestsData,
    pastMedicalHistory,
    familyHistory,
    socialHistory,
    medicationHistory,
    mediaData,
    referralDoctors,
  ]);
  return (
    <>
      <DesktopHeader
        headerContent={
          <Flex alignItems="center" gap={"12px"}>
            <Heading color={colors.grey}>Patient</Heading>
            <Heading color={colors.grey}>{`>`}</Heading>
            <Heading>{data?.data?.name || "Patient Name"}</Heading>
          </Flex>
        }
      />
      {activeAdmission && (
        <Badge colorScheme="green" ml={2}>
          Inpatient
        </Badge>
      )}

      {/* 🔥 Now passes rich seedText into admission flow */}
      <PatientAdmissionCard
        patientId={patientIdFromRoute}
        appointmentId={appointmentId}
        seedText={seedText}
      />

      <Flex
        className="p-[30px] h-full w-full"
        style={{ backgroundColor: colors.lightGrey }}
        gap={"30px"}
        direction={"column"}
      >
        <SubHeading size="s">Appointment Details</SubHeading>

        <Flex gap={"20px"}>
          {patientAppointmentDetails.map((item, index) => (
            <PatientAppointmentCard
              key={index}
              img={item.image}
              title={item.title}
              value={item.value}
            />
          ))}
        </Flex>

        <Box>
          <Flex justifyContent={"space-between"} mb={"16px"} align={"center"}>
            <Select
              width={"180px"}
              border={`3px solid ${colors.primary}`}
              textColor={colors.primary}
              focusBorderColor={colors.primary}
              onChange={handleSelectChange}
              value={selectedComplaint}
            >
              <option value="Main complaint">Main complaint</option>
              <option value="Other complaints">Other complaints</option>
              <option value="Past Medical History">Past Medical History</option>
              <option value="Family History">Family History</option>
              <option value="Social History">Social History</option>
              <option value="Medication History">Medication History</option>
              <option value="Review of Systems">Review of Systems</option>
              <option value="Physical Findings">Physical Findings</option>
              <option value="Media">Media</option>
              <option value="Differential Diagnosis">
                Differential Diagnosis
              </option>
              <option value="Tests">Tests</option>
              <option value="Definitive Diagnosis">Definitive Diagnosis</option>
              <option value="Treatment">Treatment</option>
              <option value="Refer Patient">Refer Patient</option>
            </Select>
            <Text
              color={colors.primary}
              fontWeight={700}
              fontSize={"16px"}
              fontFamily={"lato"}
              cursor={"pointer"}
              onClick={() => {
                navigate(
                  `/patients/${patientIdFromRoute}/appointments/${appointmentId}/details`,
                );
              }}
            >
              View History
            </Text>
          </Flex>

          {selectedComplaint === "Main complaint" && (
            <>
              <AppointmentComplaintsCard
                patientGender={patientGender}
                onPregnancyStatusChange={(status) => setPregnancyStatus(status)}
                pregnancyStatus={pregnancyStatus}
                onValidationChange={(isValid) => setMainComplaintValid(isValid)}
                appointmentId={appointmentId}
                patientAge={patientAge}
                formik={formik}
                bodyParts={bodyParts}
                loadingBodyParts={loadingBodyParts}
              />
              <Text fontSize="sm" color="gray.600" mt={4} px={4}>
                Note: The Main Complaint (selected condition and/or notes) can
                only be saved once per appointment. You can add or edit it later
                if you skip it now, but subsequent edits are not allowed after
                the first save.
              </Text>
            </>
          )}
          {selectedComplaint === "Other complaints" && (
            <>
              {formik?.values?.otherComplaints.map((complaint, index) => (
                <Flex
                  key={index}
                  mb="20px"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <AppointmentComplaintsCard
                    title={"Other complaints"}
                    pregnancyStatus={pregnancyStatus}
                    patientGender={patientGender}
                    appointmentId={appointmentId}
                    currentOtherComplaint={index}
                    formik={formik}
                    bodyParts={bodyParts}
                    loadingBodyParts={loadingBodyParts}
                  />

                  <DeleteIcon
                    color={colors.primary}
                    cursor="pointer"
                    onClick={() => handleDeleteComplaint(index)}
                  />
                </Flex>
              ))}
              <Text
                color={colors.primary}
                fontWeight={700}
                fontFamily={"lato"}
                fontSize={"16px"}
                mt="20px"
                cursor="pointer"
                onClick={() =>
                  formik?.setFieldValue("otherComplaints", [
                    ...formik?.values?.otherComplaints,
                    {},
                  ])
                }
              >
                + Add new complaint
              </Text>
            </>
          )}

          {selectedComplaint !== "Main complaint" &&
            selectedComplaint !== "Other complaints" && (
              <MedicalHistoryPage
                selectedHistory={selectedHistory}
                onChange={handleSelectChange}
                appointmentId={appointmentId}
                patientGender={patientGender}
                patientId={objectIdPatientId || ""}
              />
            )}

          {((selectedComplaint === "Other complaints" &&
            formik?.values?.otherComplaints?.length >= 1) ||
            selectedComplaint === "Main complaint") && (
            <Flex style={{ justifyContent: "flex-end" }}>
              <Button
                type="submit"
                theme="primary"
                text="Submit"
                className="rounded-md mt-2 w-[150px]"
                onClick={() => formik?.handleSubmit()}
                loading={isPending}
              />
            </Flex>
          )}
        </Box>
      </Flex>
    </>
  );
};

export default PatientAppointmentDetailsPage;
